/**
 * Derive perceived brightness from a CSS color for UI contrast (carousel nav, etc.).
 * Uses WCAG 2 relative luminance; returns null if the string isn't parseable.
 */

function clamp255(x: number) {
  return Math.min(255, Math.max(0, Math.round(x)))
}

function parseRgbFromCssColor(css: string): [number, number, number] | null {
  const s = css.trim()
  let m = /^#([\da-f]{3})$/i.exec(s)
  if (m) {
    const h = m[1]!
    return [
      parseInt(`${h[0]}${h[0]}`, 16),
      parseInt(`${h[1]}${h[1]}`, 16),
      parseInt(`${h[2]}${h[2]}`, 16)
    ]
  }
  m = /^#([\da-f]{6})$/i.exec(s)
  if (m) {
    const h = m[1]!
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16)
    ]
  }
  m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(s)
  if (m)
    return [clamp255(+m[1]!), clamp255(+m[2]!), clamp255(+m[3]!)] as [
      number,
      number,
      number
    ]
  return null
}

/** sRGB channels 0–255 → WCAG linear channel 0–1 */
function linearizeChannel(c255: number) {
  const c = c255 / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

export function relativeLuminanceFromCssColor(css: string): number | null {
  const rgb = parseRgbFromCssColor(css)
  if (!rgb) return null

  const r = linearizeChannel(rgb[0])
  const g = linearizeChannel(rgb[1])
  const b = linearizeChannel(rgb[2])

  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Content behind dark canvases prefers white accents; lighter canvases use brand blue (`camp-primary`). */
export type SectionSurfaceTone = "light" | "dark"

/** Treat missing / unparseable color as a light Section (carousel nav stays blue). */
export function deriveSectionSurfaceTone(
  backgroundColor?: string | null
): SectionSurfaceTone {
  const trimmed = backgroundColor?.trim()
  if (!trimmed) return "light"

  const L = relativeLuminanceFromCssColor(trimmed)
  if (L === null) return "light"

  return L < 0.42 ? "dark" : "light"
}
